import React, { useEffect } from 'react';
// eslint-disable-next-line jira/import/no-restricted-import
import { SmartCardProvider } from '@atlaskit/link-provider';
import type { ProductType } from '@atlaskit/linking-common';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useGlobalObjectPreviewPanels,
	usePreviewPanelIFrameCommunication,
	type GlobalObjectType,
} from '@atlassian/preview-panel-global-objects';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api';

const SUPPORT_PANEL_TYPES_MILESTONE_ONE: GlobalObjectType[] = [
	'identity-team',
	'confluence-page',
	'confluence-whiteboard',
	'townsquare-goal',
	'townsquare-project',
];

/**
 * Only return the route name if the route is preview panel enabled.
 */
export const useEnabledRoute = createRouterSelector(({ route }) =>
	route.supportsPreviewPanel ? route.name : null,
);

export const LinkingProviders = ({ children }: { children: React.ReactNode }) => {
	const { data } = useProjectContext();

	const product = getProductForSmartCardProvider(data);

	if (fg('jira-glance-panel')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { close } = usePreviewPanelsActions();
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const enabledRouteName = useEnabledRoute();

		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { isPreviewPanelAvailable, openPreviewPanel } = useGlobalObjectPreviewPanels('jira', {
			supportedObjects: fg('jira-glance-issue-panel')
				? undefined
				: SUPPORT_PANEL_TYPES_MILESTONE_ONE,
			enabled: enabledRouteName !== null,
		});

		// eslint-disable-next-line react-hooks/rules-of-hooks
		usePreviewPanelIFrameCommunication(openPreviewPanel);

		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(
			() => () => {
				// automatically close the preview panel when the route changes
				close();
			},
			[enabledRouteName, close],
		);

		return (
			<SmartCardProvider
				isPreviewPanelAvailable={isPreviewPanelAvailable}
				openPreviewPanel={openPreviewPanel}
				product={product}
				isAdminHubAIEnabled={data?.isAIEnabled ?? false}
			>
				{children}
			</SmartCardProvider>
		);
	}

	return (
		<SmartCardProvider product={product} isAdminHubAIEnabled={data?.isAIEnabled ?? false}>
			{children}
		</SmartCardProvider>
	);
};

const getProductForSmartCardProvider = (
	project: ProjectContext | null | undefined,
): ProductType | undefined => {
	switch (project?.projectType) {
		case 'software':
			return 'JSW';
		case 'business':
			return 'JWM';
		case 'service_desk':
			return 'JSM';
		case 'product_discovery':
			return 'JPD';
		default:
			return undefined;
	}
};
